import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FileText, CheckCircle, AlertTriangle, Clock, Loader,
  Shield, Phone, Mail
} from 'lucide-react';
import SignaturePad from '../components/SignaturePad';
import api from '../services/api';

/**
 * Public Agreement Signing Page
 * Customers access this via email link - no login required
 */
const SignAgreementPage = () => {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  
  // Form state
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureTypedName, setSignatureTypedName] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [photoConsent, setPhotoConsent] = useState(false);
  const [cancellationAccepted, setCancellationAccepted] = useState(false);
  const [weatherAccepted, setWeatherAccepted] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    fetchAgreement();
  }, [token]);

  const fetchAgreement = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/serviceagreements/sign/${token}`);
      
      if (response.data.status === 'already_signed') {
        setAlreadySigned(true);
        setSuccessData(response.data);
      } else {
        setAgreement(response.data);
        setSignerName(response.data.customerName || '');
        setSignerEmail(response.data.customerEmail || '');
      }
    } catch (err) {
      console.error('Error fetching agreement:', err);
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.error || 'Unable to load agreement';
      
      if (errorCode === 'EXPIRED') {
        setError({ type: 'expired', message: errorMessage });
      } else if (errorCode === 'VOIDED') {
        setError({ type: 'voided', message: errorMessage });
      } else if (errorCode === 'NOT_FOUND' || err.response?.status === 404) {
        setError({ type: 'not_found', message: 'Agreement not found' });
      } else {
        setError({ type: 'error', message: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureChange = (imageData, typedName) => {
    setSignatureImage(imageData);
    setSignatureTypedName(typedName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!signatureImage && !signatureTypedName) {
      alert('Please provide your signature');
      return;
    }
    
    if (!signerName.trim()) {
      alert('Please enter your full legal name');
      return;
    }
    
    if (!agreedToTerms) {
      alert('Please agree to the terms to continue');
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await api.post(`/serviceagreements/sign/${token}`, {
        signatureImage,
        signatureTypedName,
        signerName: signerName.trim(),
        signerEmail: signerEmail.trim() || null,
        photoConsentGiven: agreement.includePhotoConsent ? photoConsent : null,
        cancellationPolicyAccepted: agreement.includeCancellationPolicy ? cancellationAccepted : null,
        weatherPolicyAccepted: agreement.includeWeatherPolicy ? weatherAccepted : null
      });
      
      setSuccess(true);
      setSuccessData(response.data);
    } catch (err) {
      console.error('Error submitting signature:', err);
      alert(err.response?.data?.error || 'Failed to submit signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          {error.type === 'expired' ? (
            <>
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h1>
              <p className="text-gray-600 mb-6">{error.message}</p>
              <p className="text-sm text-gray-500">
                Please contact the service provider to request a new agreement link.
              </p>
            </>
          ) : error.type === 'voided' ? (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Agreement Voided</h1>
              <p className="text-gray-600 mb-6">{error.message}</p>
              <p className="text-sm text-gray-500">
                This agreement is no longer valid. Please contact the service provider.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Agreement Not Found</h1>
              <p className="text-gray-600">{error.message}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Already signed / Success state
  if (alreadySigned || success) {
    const data = successData || {};
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {success ? 'Agreement Signed!' : 'Already Signed'}
          </h1>
          <p className="text-gray-600 mb-6">
            {success 
              ? 'Thank you for signing. A confirmation has been sent to your email.'
              : `This agreement was signed on ${new Date(data.signedAt).toLocaleDateString()}.`
            }
          </p>
          
          {data.businessName && (
            <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
              <p className="text-sm text-gray-600 mb-2">Service Provider:</p>
              <p className="font-semibold text-gray-900">{data.businessName}</p>
              {data.businessPhone && (
                <p className="text-sm text-gray-600 flex items-center gap-2 mt-2">
                  <Phone className="h-4 w-4" />
                  {data.businessPhone}
                </p>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-center gap-2 text-green-600">
            <Shield className="h-5 w-5" />
            <span className="text-sm">Secure & Legally Binding</span>
          </div>
        </div>
      </div>
    );
  }

  // Main signing form
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">{agreement.businessName}</h1>
                <p className="text-sm text-gray-500">Service Agreement</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Shield className="h-4 w-4 text-green-600" />
              Secure
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          {/* Agreement Title */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div className="bg-blue-600 text-white px-6 py-4">
              <h2 className="text-xl font-bold">{agreement.agreementTitle}</h2>
              <p className="text-blue-100 text-sm mt-1">
                Please review and sign below
              </p>
            </div>
            
            {/* Expiration notice */}
            <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-100 flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-700">
                This link expires on {new Date(agreement.expiresAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Agreement Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: agreement.agreementContent }}
            />
          </div>

          {/* Optional Consents */}
          {(agreement.includePhotoConsent || agreement.includeCancellationPolicy || agreement.includeWeatherPolicy) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Additional Acknowledgments</h3>
              <div className="space-y-4">
                {agreement.includeCancellationPolicy && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cancellationAccepted}
                      onChange={(e) => setCancellationAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I acknowledge and accept the cancellation policy
                    </span>
                  </label>
                )}
                
                {agreement.includeWeatherPolicy && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={weatherAccepted}
                      onChange={(e) => setWeatherAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I understand services may be rescheduled due to weather conditions
                    </span>
                  </label>
                )}
                
                {agreement.includePhotoConsent && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={photoConsent}
                      onChange={(e) => setPhotoConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I consent to before/after photos being used for marketing purposes (optional)
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Signature Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Your Signature</h3>
            
            {/* Signer Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Legal Name *
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Signature Pad */}
            <SignaturePad onSignatureChange={handleSignatureChange} />
          </div>

          {/* Terms Agreement */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 mb-6 p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                required
                className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <strong>I confirm that:</strong> I have read and understand this agreement, I am authorized to sign on behalf of the customer listed above, and I agree to the terms and conditions stated herein. I understand this constitutes a legally binding electronic signature.
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !agreedToTerms || (!signatureImage && !signatureTypedName)}
            className="w-full bg-blue-600 text-white py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Sign Agreement
              </>
            )}
          </button>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Shield className="h-4 w-4 text-green-600" />
              Your signature is encrypted and legally binding
            </div>
            <p className="text-xs text-gray-400 mt-2">
              IP address and timestamp will be recorded for verification
            </p>
          </div>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Powered by <span className="font-semibold text-blue-600">SuddyFlow</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Secure electronic signature service
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SignAgreementPage;